/**
 * Rahmen für die Sandbox-Sitzung.
 *
 * Zwei Aufgaben:
 *
 * 1. **`SandboxMsalProvider`** – die App fragt an rund zwei Dutzend Stellen über
 *    `useMsal()` nach dem angemeldeten Konto (Adresse, Name, Mandant, Abmelden).
 *    In der Sandbox gibt es kein Microsoft-Konto. Statt alle diese Stellen
 *    umzubauen, wird hier der MSAL-Kontext für den Sandbox-Teilbaum mit einem
 *    synthetischen Konto überschrieben. Alles darunter arbeitet unverändert
 *    weiter – es merkt schlicht nicht, dass niemand bei Microsoft angemeldet ist.
 *
 *    Wichtig: Das synthetische Konto ist reine Anzeige. Wer hier wirklich was
 *    darf, entscheidet der Server allein anhand des Sandbox-Tokens
 *    (`api/_sandbox.mjs`). Diese Datei zu manipulieren ändert die Oberfläche,
 *    aber keine Berechtigung.
 *
 * 2. **`SandboxBanner`** – dauerhaft sichtbarer Hinweis, dass das hier
 *    Testdaten sind, plus Rollenwechsel und Restlaufzeit. Der Hinweis ist
 *    Absicht: Eine Ansicht, die genau wie die echte aussieht, aber erfundene
 *    Zahlen zeigt, verwechselt man sonst irgendwann.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { MsalContext, useMsal } from "@azure/msal-react";
import { InteractionStatus, type AccountInfo, type IPublicClientApplication } from "@azure/msal-browser";
import { authFetch } from "../utils/authFetch";
import {
  availableSandboxRoles,
  sandboxDisplayName,
  sandboxRoleLabel,
  clearSandboxSession,
  getSandboxSession,
  sandboxExpiresAt,
  sandboxMailPreview,
  sandboxRole,
  sandboxToken,
  setSandboxRole,
} from "../utils/sandboxSession";

const SANDBOX_COLOR = "#B45309";

type TokenClaims = { aud?: string; sub?: string; role?: string; uid?: string };

function decodeClaims(token: string | null): TokenClaims {
  if (!token) return {};
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as TokenClaims;
  } catch {
    return {};
  }
}

/** Beendet die Sandbox und kehrt auf die normale Anmeldung zurück. */
export function exitSandbox(): void {
  clearSandboxSession();
  try {
    window.location.assign("/");
  } catch {
    window.location.reload();
  }
}

export function SandboxMsalProvider({ children }: { children: ReactNode }) {
  const { instance, logger } = useMsal();
  const role = sandboxRole() ?? "admin";
  const claims = useMemo(() => decodeClaims(sandboxToken()), []);

  const account = useMemo<AccountInfo>(() => ({
    homeAccountId: `sandbox.${role}`,
    environment: "sandbox",
    // `aud` des Tokens ist die Sandbox-tenant_id – dieselbe, mit der der Server
    // rechnet. Damit fragt die App auch genau diesen Mandanten ab.
    tenantId: claims.aud ?? "",
    username: claims.sub ?? "",
    // Die Objekt-ID der Person: daran haengt die Zuweisung des Trainingsplans
    // (user_level_assignments), und damit, welche Module sichtbar sind. Sie
    // kommt aus dem Token, damit die IDs nicht an einer dritten Stelle
    // gepflegt werden muessen.
    localAccountId: claims.uid ?? `sandbox.${role}`,
    name: sandboxDisplayName() || role,
    idTokenClaims: {
      oid: claims.uid ?? "",
      // Nur der Administrator trägt die App-Rolle. Für die tatsächliche
      // Berechtigung ist weiterhin der Server zuständig; hier geht es darum,
      // dass die Oberfläche dieselben Menüs registriert wie im Echtbetrieb.
      roles: role === "admin" ? ["Admin"] : [],
      tid: claims.aud ?? "",
      preferred_username: claims.sub ?? "",
    },
  }), [role, claims]);

  // Das echte MSAL-Objekt bleibt erhalten (die Anwendung ruft daran noch
  // andere Methoden auf); überschrieben wird nur, was ohne Microsoft-Konto
  // nicht funktionieren kann oder in der Sandbox etwas anderes bedeutet.
  const proxiedInstance = useMemo<IPublicClientApplication>(() => new Proxy(instance, {
    get(target, prop, receiver) {
      if (prop === "getActiveAccount") return () => account;
      if (prop === "getAllAccounts") return () => [account];
      if (prop === "getAccount") return () => account;
      if (prop === "logoutRedirect" || prop === "logoutPopup") {
        return async () => { exitSandbox(); };
      }
      // Ohne Konto gibt es keine Microsoft-Tokens. Die Aufrufer fangen das ab
      // (authFetch nutzt in der Sandbox ohnehin den eigenen Weg); ein stiller
      // Redirect zur Microsoft-Anmeldung wäre hier das falsche Verhalten.
      if (prop === "acquireTokenSilent" || prop === "acquireTokenPopup") {
        return async () => { throw new Error("sandbox_no_msal_token"); };
      }
      if (prop === "acquireTokenRedirect" || prop === "loginRedirect" || prop === "loginPopup") {
        return async () => { /* in der Sandbox bewusst wirkungslos */ };
      }
      const value = Reflect.get(target, prop, receiver);
      return typeof value === "function" ? value.bind(target) : value;
    },
  }), [instance, account]);

  const value = useMemo(() => ({
    instance: proxiedInstance,
    accounts: [account],
    inProgress: InteractionStatus.None,
    logger,
  }), [proxiedInstance, account, logger]);

  return <MsalContext.Provider value={value}>{children}</MsalContext.Provider>;
}

function formatRemaining(ms: number): string {
  if (ms <= 0) return "abgelaufen";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `noch ${minutes} Min.`;
  return `noch ${Math.floor(minutes / 60)} Std. ${minutes % 60} Min.`;
}

export function SandboxBanner() {
  const roles = availableSandboxRoles();
  const active = sandboxRole();
  const expiresAt = sandboxExpiresAt();
  const [now, setNow] = useState(() => Date.now());
  const path = window.location.pathname.replace(/\/+$/, "");
  const isAdminConcept = path === "/admin-konzept";
  const isRework = path === "/rework";

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  if (!active) return null;
  const remaining = expiresAt ? expiresAt - now : null;

  return (
    <div
      style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 99998,
        background: SANDBOX_COLOR, color: "#FFFFFF",
        padding: "0.5rem 1rem",
        display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
        fontSize: "0.8rem", boxShadow: "0 -2px 12px rgba(0,0,0,0.18)",
      }}
    >
      <strong style={{ letterSpacing: "0.04em" }}>SANDBOX</strong>
      <span style={{ opacity: 0.9 }}>Testdaten – keine echten Personen, kein Mailversand</span>

      <span style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginLeft: "auto" }}>
        <span style={{ opacity: 0.85 }}>Ansicht:</span>
        {roles.length <= 4 ? roles.map((role) => (
          <button
            key={role}
            type="button"
            onClick={() => { if (role !== active) setSandboxRole(role, isRework ? "/rework" : undefined); }}
            aria-pressed={role === active}
            style={{
              padding: "0.25rem 0.6rem",
              borderRadius: "999px",
              border: `1px solid ${role === active ? "#FFFFFF" : "rgba(255,255,255,0.45)"}`,
              background: role === active ? "#FFFFFF" : "transparent",
              color: role === active ? SANDBOX_COLOR : "#FFFFFF",
              fontSize: "0.75rem",
              fontWeight: role === active ? 700 : 500,
              cursor: role === active ? "default" : "pointer",
            }}
          >
            {sandboxRoleLabel(role)}
          </button>
        )) : (
          <select
            value={active}
            onChange={(event) => setSandboxRole(event.target.value, isRework ? "/rework" : undefined)}
            aria-label="Testidentität wechseln"
            style={{
              padding: "0.25rem 0.5rem", borderRadius: "6px", border: "1px solid #FFFFFF",
              background: "#FFFFFF", color: SANDBOX_COLOR, fontSize: "0.75rem", fontWeight: 700, maxWidth: "260px",
            }}
          >
            {roles.map((role) => <option key={role} value={role}>{sandboxRoleLabel(role)}</option>)}
          </select>
        )}
      </span>

      <SandboxInboxButton />

      {remaining !== null && (
        <span style={{ opacity: 0.85, fontVariantNumeric: "tabular-nums" }}>{formatRemaining(remaining)}</span>
      )}
      <button
        type="button"
        onClick={() => window.location.assign(isRework ? (active === "admin" ? "/admin" : "/dashboard") : "/rework")}
        aria-current={isRework ? "page" : undefined}
        style={{
          padding: "0.25rem 0.65rem", borderRadius: "6px",
          border: "1px solid #FFFFFF", background: isRework ? "#FFFFFF" : "transparent",
          color: isRework ? SANDBOX_COLOR : "#FFFFFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
        }}
      >
        {isRework ? "Zur aktuellen App" : "Rework-Entwurf"}
      </button>
      {active === "admin" && !isRework && (
        <button
          type="button"
          onClick={() => window.location.assign(isAdminConcept ? "/admin" : "/admin-konzept")}
          aria-current={isAdminConcept ? "page" : undefined}
          style={{
            padding: "0.25rem 0.65rem", borderRadius: "6px",
            border: "1px solid #FFFFFF", background: isAdminConcept ? "#FFFFFF" : "transparent",
            color: isAdminConcept ? SANDBOX_COLOR : "#FFFFFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
          }}
        >
          {isAdminConcept ? "Zur Admin-Sandbox" : "Admin-Konzept testen"}
        </button>
      )}
      <button
        type="button"
        onClick={exitSandbox}
        style={{
          padding: "0.25rem 0.6rem", borderRadius: "6px",
          border: "1px solid rgba(255,255,255,0.45)", background: "transparent",
          color: "#FFFFFF", fontSize: "0.75rem", fontWeight: 600, cursor: "pointer",
        }}
      >
        Beenden
      </button>
    </div>
  );
}

type InboxMail = { id: string; at: string; kind: string; to: string[]; from: string; subject: string; html?: string };

const MAIL_KIND_LABELS: Record<string, string> = {
  status: "Monatsmail",
  reminder: "Erinnerung der Führungskraft",
  phishing: "Phishing-Simulation",
  "module-update": "Modul-Update",
  operational: "Betriebsmail",
};

/**
 * Postfach der aktiven Testidentität: alles, was AWARE ihr geschickt hätte.
 * Echte Mails gibt es in der Sandbox nicht – der Mailer legt sie hier ab
 * (`api/_sandboxOutbox.js`). Links in Mails funktionieren, z.B. der Klick auf
 * eine Phishing-Simulation, und zählen dann für genau diese Person.
 */
function SandboxInboxButton() {
  const [open, setOpen] = useState(false);
  const [mails, setMails] = useState<InboxMail[] | null>(null);
  const [selected, setSelected] = useState<InboxMail | null>(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    try {
      const res = await authFetch("/api/sync-users?job=sandbox-inbox");
      const data = await res.json() as { mails?: InboxMail[]; error?: string };
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setMails(data.mails ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const show = async (mail: InboxMail) => {
    setSelected(mail);
    try {
      const res = await authFetch(`/api/sync-users?job=sandbox-inbox&id=${encodeURIComponent(mail.id)}`);
      const data = await res.json() as { mail?: InboxMail };
      if (res.ok && data.mail) setSelected(data.mail);
    } catch {
      // Liste bleibt stehen, nur die Vorschau fehlt.
    }
  };

  useEffect(() => { void load(); }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); void load(); }}
        style={{
          padding: "0.25rem 0.65rem", borderRadius: "6px",
          border: "1px solid #FFFFFF", background: "transparent",
          color: "#FFFFFF", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer",
        }}
      >
        ✉ Postfach{mails && mails.length > 0 ? ` (${mails.length})` : ""}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="Sandbox-Postfach"
          style={{ position: "fixed", inset: 0, zIndex: 99999, background: "rgba(17,24,39,0.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{ background: "#FFFFFF", color: "#1A1A2E", borderRadius: "12px", width: "min(1000px, 100%)", height: "min(720px, 100%)", display: "flex", flexDirection: "column", overflow: "hidden" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.75rem 1rem", borderBottom: "1px solid #E5E7EB" }}>
              <strong style={{ fontSize: "0.95rem" }}>Postfach von {sandboxDisplayName() || "dieser Identität"}</strong>
              <span style={{ fontSize: "0.75rem", color: "#6B7280" }}>Testmails – nichts davon wurde wirklich verschickt</span>
              <button type="button" onClick={() => void load()} style={{ marginLeft: "auto", fontSize: "0.75rem", padding: "0.3rem 0.6rem", borderRadius: "6px", border: "1px solid #D1D5DB", background: "#FFFFFF", cursor: "pointer" }}>Aktualisieren</button>
              <button type="button" onClick={() => setOpen(false)} style={{ fontSize: "0.75rem", padding: "0.3rem 0.6rem", borderRadius: "6px", border: "1px solid #D1D5DB", background: "#FFFFFF", cursor: "pointer" }}>Schließen</button>
            </div>
            <div style={{ display: "flex", flex: 1, minHeight: 0, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 280px", maxWidth: "360px", borderRight: "1px solid #E5E7EB", overflowY: "auto" }}>
                {error && <p style={{ padding: "1rem", fontSize: "0.8rem", color: "#DC2626" }}>{error}</p>}
                {mails && mails.length === 0 && (
                  <p style={{ padding: "1rem", fontSize: "0.8rem", color: "#6B7280", lineHeight: 1.6 }}>
                    Noch keine Mails. Im GA-Portal unter Sandbox → Benachrichtigungen die Monatsmail auslösen,
                    als Führungskraft eine Erinnerung schicken oder als Admin eine Phishing-Kampagne starten.
                  </p>
                )}
                {(mails ?? []).map((mail) => (
                  <button
                    key={mail.id}
                    type="button"
                    onClick={() => void show(mail)}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "0.65rem 1rem", border: "none", borderBottom: "1px solid #F3F4F6", background: selected?.id === mail.id ? "#FEF3C7" : "#FFFFFF", cursor: "pointer" }}
                  >
                    <div style={{ fontSize: "0.7rem", color: "#B45309", fontWeight: 700 }}>{MAIL_KIND_LABELS[mail.kind] ?? mail.kind}</div>
                    <div style={{ fontSize: "0.82rem", fontWeight: 600, margin: "0.15rem 0" }}>{mail.subject || "(ohne Betreff)"}</div>
                    <div style={{ fontSize: "0.7rem", color: "#6B7280" }}>{new Date(mail.at).toLocaleString("de-DE")} · {mail.from}</div>
                  </button>
                ))}
              </div>
              <div style={{ flex: "2 1 360px", minWidth: 0, display: "flex" }}>
                {selected?.html ? (
                  <iframe
                    title={selected.subject}
                    srcDoc={sandboxMailPreview(selected.html)}
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    style={{ border: "none", width: "100%", height: "100%" }}
                  />
                ) : (
                  <p style={{ margin: "auto", fontSize: "0.8rem", color: "#9CA3AF" }}>{selected ? "Lädt…" : "Mail auswählen"}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** Sitzung abgelaufen oder Token kaputt: zurück ins GA-Portal. */
export function SandboxExpiredScreen() {
  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-page, #F4F7FA)", display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <div style={{ background: "var(--bg-card, #FFFFFF)", border: "1px solid var(--border, #E5E7EB)", borderRadius: "16px", padding: "2.5rem", maxWidth: "420px", textAlign: "center" }}>
        <div style={{ fontSize: "2rem", marginBottom: "0.75rem" }} aria-hidden>🧪</div>
        <h1 style={{ fontSize: "1.25rem", fontWeight: 700, margin: "0 0 0.5rem 0", color: "var(--text-primary, #111827)" }}>
          Sandbox-Sitzung abgelaufen
        </h1>
        <p style={{ fontSize: "0.875rem", color: "var(--text-secondary, #6B7280)", lineHeight: 1.6, margin: 0 }}>
          Sandbox-Zugänge gelten eine Stunde und werden nicht verlängert.
          Im GA-Portal unter „Sandbox" lässt sich einfach ein neuer öffnen.
        </p>
      </div>
    </div>
  );
}

export function hasSandboxSession(): boolean {
  return getSandboxSession() !== null;
}
